import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';

const typeOptions = [
  { value: 'undang-undang', label: 'Undang-Undang' },
  { value: 'peraturan-pemerintah', label: 'Peraturan Pemerintah' },
  { value: 'perpres', label: 'Peraturan Presiden' },
  { value: 'permen', label: 'Peraturan Menteri' },
  { value: 'perka-lkpp', label: 'Peraturan LKPP' },
  { value: 'perdirjen', label: 'Perdirjen' },
  { value: 'se', label: 'Surat Edaran' },
  { value: 'pedoman', label: 'Pedoman' },
  { value: 'lainnya', label: 'Lainnya' }
];

const emptyForm = { title: '', number: '', type: 'perpres', year: new Date().getFullYear(), description: '', content: '', status: 'berlaku', tags: '' };

const AdminRegulationsPage = () => {
  const [regulations, setRegulations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({});
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (search) params.search = search;
      const { data } = await api.get('/regulations', { params });
      setRegulations(data.regulations);
      setPagination(data.pagination);
    } catch (err) { console.error(err); } finally { setLoading(false); }
  }, [page, search]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreate = () => {
    setForm(emptyForm);
    setEditId(null);
    setFile(null);
    setShowForm(true);
  };

  const openEdit = (reg) => {
    setForm({
      title: reg.title, number: reg.number, type: reg.type,
      year: reg.year, description: reg.description || '',
      content: reg.content || '', status: reg.status,
      tags: Array.isArray(reg.tags) ? reg.tags.join(', ') : ''
    });
    setEditId(reg.id);
    setFile(null);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const formData = new FormData();
      Object.entries(form).forEach(([k, v]) => {
        if (k === 'tags') {
          formData.append(k, JSON.stringify(v.split(',').map(t => t.trim()).filter(Boolean)));
        } else {
          formData.append(k, v);
        }
      });
      if (file) formData.append('file', file);

      if (editId) {
        await api.put(`/regulations/${editId}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        toast.success('Peraturan diperbarui');
      } else {
        await api.post('/regulations', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        toast.success('Peraturan ditambahkan');
      }
      setShowForm(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Gagal menyimpan');
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/regulations/${deleteConfirm.id}`);
      toast.success('Peraturan dihapus');
      setDeleteConfirm(null);
      fetchData();
    } catch (err) { toast.error('Gagal menghapus'); }
  };

  const set = (key) => (e) => setForm(p => ({ ...p, [key]: e.target.value }));

  return (
    <div className="fade-in">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <h2 style={{ fontSize: '1.125rem', fontWeight: 700 }}>Kelola Peraturan</h2>
        <button className="btn btn-primary" onClick={openCreate}>
          <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" /></svg>
          Tambah Peraturan
        </button>
      </div>

      {/* Search */}
      <div className="card" style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
          <div className="form-group" style={{ flex: 1 }}>
            <label className="form-label">Cari</label>
            <input className="form-input" placeholder="Judul, nomor..." value={search}
              onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && fetchData()} />
          </div>
          <button className="btn btn-primary btn-sm" onClick={() => { setPage(1); fetchData(); }}>Cari</button>
        </div>
      </div>

      {loading ? (
        <div className="loading-screen"><div className="spinner" /><span>Memuat...</span></div>
      ) : (
        <>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 40 }}>No</th>
                  <th>Judul</th>
                  <th>Nomor</th>
                  <th>Jenis</th>
                  <th>Tahun</th>
                  <th>Status</th>
                  <th>Unduhan</th>
                  <th style={{ width: 120 }}>Aksi</th>
                </tr>
              </thead>
              <tbody>
                {regulations.map((r, i) => (
                  <tr key={r.id}>
                    <td>{(page - 1) * 20 + i + 1}</td>
                    <td style={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 600 }}>{r.title}</td>
                    <td style={{ fontFamily: 'var(--font-mono)', fontSize: '0.8125rem' }}>{r.number}</td>
                    <td><span className="badge badge-orange">{typeOptions.find(t => t.value === r.type)?.label || r.type}</span></td>
                    <td>{r.year}</td>
                    <td><span className={`badge ${r.status === 'berlaku' ? 'badge-green' : r.status === 'dicabut' ? 'badge-red' : 'badge-yellow'}`}>{r.status}</span></td>
                    <td>{r.download_count}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="btn btn-ghost btn-sm btn-icon" title="Edit" onClick={() => openEdit(r)}>
                          <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931z" /></svg>
                        </button>
                        <button className="btn btn-ghost btn-sm btn-icon" title="Hapus" onClick={() => setDeleteConfirm(r)} style={{ color: 'var(--error)' }}>
                          <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" /></svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {pagination.pages > 1 && (
            <div className="pagination">
              <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}>&laquo;</button>
              {Array.from({ length: Math.min(pagination.pages, 7) }, (_, i) => i + 1).map(p => (
                <button key={p} className={page === p ? 'active' : ''} onClick={() => setPage(p)}>{p}</button>
              ))}
              <button disabled={page >= pagination.pages} onClick={() => setPage(p => p + 1)}>&raquo;</button>
            </div>
          )}
        </>
      )}

      {/* Create/Edit Modal */}
      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal" style={{ maxWidth: 640, maxHeight: '90vh' }} onClick={e => e.stopPropagation()}>
            <h2 className="modal-title">{editId ? 'Edit Peraturan' : 'Tambah Peraturan Baru'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group" style={{ marginBottom: 14 }}>
                <label className="form-label">Judul Peraturan *</label>
                <input className="form-input" value={form.title} onChange={set('title')} required />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
                <div className="form-group">
                  <label className="form-label">Nomor *</label>
                  <input className="form-input" placeholder="Perpres No. 12/2021" value={form.number} onChange={set('number')} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Tahun *</label>
                  <input type="number" className="form-input" value={form.year} onChange={set('year')} required />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
                <div className="form-group">
                  <label className="form-label">Jenis *</label>
                  <select className="form-select" value={form.type} onChange={set('type')}>
                    {typeOptions.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Status</label>
                  <select className="form-select" value={form.status} onChange={set('status')}>
                    <option value="berlaku">Berlaku</option>
                    <option value="dicabut">Dicabut</option>
                    <option value="diubah">Diubah</option>
                  </select>
                </div>
              </div>
              <div className="form-group" style={{ marginBottom: 14 }}>
                <label className="form-label">Deskripsi</label>
                <textarea className="form-textarea" value={form.description} onChange={set('description')} style={{ minHeight: 80 }} />
              </div>
              <div className="form-group" style={{ marginBottom: 14 }}>
                <label className="form-label">Isi / Ringkasan (HTML)</label>
                <textarea className="form-textarea" value={form.content} onChange={set('content')} style={{ minHeight: 120, fontFamily: 'var(--font-mono)', fontSize: '0.8125rem' }} />
              </div>
              <div className="form-group" style={{ marginBottom: 14 }}>
                <label className="form-label">Tags (pisahkan koma)</label>
                <input className="form-input" placeholder="pengadaan, perpres, 2021" value={form.tags} onChange={set('tags')} />
              </div>
              <div className="form-group" style={{ marginBottom: 24 }}>
                <label className="form-label">File Dokumen (PDF/DOCX)</label>
                <input type="file" accept=".pdf,.doc,.docx" onChange={e => setFile(e.target.files[0])}
                  style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }} />
              </div>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>Batal</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? <div className="spinner" style={{ width: 18, height: 18, borderWidth: 2 }} /> : editId ? 'Perbarui' : 'Simpan'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteConfirm && (
        <div className="modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="modal" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
            <h2 className="modal-title" style={{ color: 'var(--error)' }}>Hapus Peraturan</h2>
            <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: 24 }}>
              Yakin ingin menghapus <strong>"{deleteConfirm.title}"</strong>?
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setDeleteConfirm(null)}>Batal</button>
              <button className="btn btn-danger" onClick={handleDelete}>Hapus</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminRegulationsPage;
